import { useEffect, useSyncExternalStore } from "react";
import { api } from "./api/client";
import type { LeadRow, OrderBrief } from "./api/types";
import { useInterval, useOnVisible } from "./hooks";
import { LINK_FRESH_MS } from "./signedLinks";

/*
 * The shop's orders, one copy for the whole console, as prices.ts keeps the
 * price list: the menu's 今天, 今天's 安排发货 and 未收, and 客户表's 今天要做,
 * 要收的钱 and 订单 count the same list read at the same moment. Each page used to
 * read its own, and 今天 counted the orders to ship only once it had been
 * opened: the menu said 13, then 14 (10/24).
 *
 * App keeps it read from sign-in for anybody who may see customers, every
 * minute; 客户表 asks again as it refreshes. Unchanged, the server answers 304
 * to the copy's tag and nothing is parsed (lazior-api list_tag.go).
 */

interface State {
  /** Newest first, as the server sends them; null until the first answer. */
  orders: OrderBrief[] | null;
  /** The back office the order numbers open in; empty = no link. */
  shopURL: string;
  /** A read has ended, answered or not: a page waiting for the orders stops waiting (an older API has no list). */
  read: boolean;
}

const EMPTY: State = { orders: null, shopURL: "", read: false };
let state: State = EMPTY;
let tag = "";
let inflight: Promise<void> | null = null;
const subs = new Set<() => void>();

const subscribe = (f: () => void) => {
  subs.add(f);
  return () => subs.delete(f);
};
const snapshot = () => state;
const set = (s: State) => {
  state = s;
  subs.forEach((f) => f());
};

/** How often App asks while somebody is signed in. */
const POLL_MS = 60_000;

/** Reads the orders; a read already on its way is the answer. Quiet: never what a press waits for. */
export function loadOrders(quiet = true): Promise<void> {
  if (inflight) return inflight;
  const p: Promise<void> = api
    .orders(tag, quiet)
    .then(
      ({ body, tag: t }) => {
        if (inflight !== p) return; // signed out meanwhile
        tag = t;
        if (body) set({ orders: body.orders ?? [], shopURL: body.shop_admin_url ?? "", read: true });
        else if (!state.read) set({ ...state, read: true });
      },
      () => {
        // The copy on screen stays; a 401 is App's to handle.
        if (inflight === p && !state.read) set({ ...state, read: true });
      },
    )
    .finally(() => {
      if (inflight === p) inflight = null;
    });
  inflight = p;
  return p;
}

/** An order changed here (its note): shown at once; the next read brings the shop's own. */
export function patchOrder(orderNo: string, patch: Partial<OrderBrief>): void {
  if (!state.orders) return;
  set({ ...state, orders: state.orders.map((o) => (o.order_no === orderNo ? { ...o, ...patch } : o)) });
}

/** Signed out: nothing of one person's is shown to the next. */
export function forgetOrders(): void {
  tag = "";
  inflight = null;
  set(EMPTY);
  forgetCustomers();
}

/**
 * The orders, for a page. `feed` keeps them read — at once, then every minute,
 * and on coming back to a tab left in the background (its timers slow to a
 * minute or more): App, for anybody who may see customers.
 */
export function useOrders(feed = false): State {
  const s = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => {
    if (feed) void loadOrders();
  }, [feed]);
  useInterval(() => !document.hidden && void loadOrders(), POLL_MS, feed, { fireOnEnable: false });
  useOnVisible(() => void loadOrders(), feed);
  return s;
}

/* ---- the customer list, a page at a time: 客户表 reads it, and 今天 for 要收的钱 ---- */

/** Customers per page: production's 652 (10/01) came in one. */
const PAGE = 1000;

/** Each page as last answered, under its tag, and when it came whole (its pictures' links were signed then). */
export type HeldPages = Map<number, { tag: string; leads: LeadRow[]; at: number }>;

/*
 * The console's one copy of the pages, as with the orders above. 客户 and 今天 each read their own: the whole list
 * twice (production's first page is 2.2 MB, 1.1–1.35 s), 客户 opened after 今天 had read it waited for all of it
 * again behind its placeholder, and the two refreshes asked the server twice. Now one read on its way is everybody's
 * answer, and a reader's copy (HeldPages) only tells it whether anything changed since it last looked. A page that
 * came whole longer ago than LINK_FRESH_MS is asked for whole again: a 304 keeps the links it had, and they run out.
 */
const shared: HeldPages = new Map();
let sharedRows: LeadRow[] | null = null;
let sharedAt = 0;
let sharedReading: Promise<void> | null = null;
/** Bumped on signing out: a read from before lands nowhere. */
let sharedGen = 0;

/** Every customer, from the console's copy if it was answered lately (客户 opening on it while it reads again). */
export function customersReadLately(maxAgeMs = 3 * 60_000): LeadRow[] | null {
  return sharedRows && Date.now() - sharedAt < maxAgeMs ? sharedRows : null;
}

/**
 * Every customer, as the API lists them, and whether that changed since `held` (the reader's own record) was filled.
 * A read already on its way is the answer.
 */
export async function readCustomerPages(held: HeldPages): Promise<{ rows: LeadRow[]; changed: boolean }> {
  if (!sharedReading) {
    const gen = sharedGen;
    // Read into a copy, taken only if nobody signed out meanwhile.
    const work: HeldPages = new Map([...shared].filter(([, v]) => Date.now() - v.at <= LINK_FRESH_MS));
    const p: Promise<void> = readPagesInto(work)
      .then(({ rows, changed }) => {
        if (gen !== sharedGen) throw new Error("signed out meanwhile");
        shared.clear();
        work.forEach((v, k) => shared.set(k, v));
        if (changed || !sharedRows) sharedRows = rows;
        sharedAt = Date.now();
      })
      .finally(() => {
        if (sharedReading === p) sharedReading = null;
      });
    sharedReading = p;
  }
  await sharedReading;
  const changed = held.size !== shared.size || [...shared].some(([k, v]) => held.get(k) !== v);
  held.clear();
  shared.forEach((v, k) => held.set(k, v));
  return { rows: sharedRows ?? [], changed };
}

/** Signed out: nobody's customers are kept for the next person. */
function forgetCustomers(): void {
  sharedGen++;
  shared.clear();
  sharedRows = null;
  sharedAt = 0;
  sharedReading = null;
}

/**
 * Every customer, as the API lists them. The pages are asked for together, not
 * each after the one before: on production the second waited 1.1–2.3 s for the
 * first (2026-10-21). As many as the last read had — two the first time, the
 * customers being more than a page — and past those, while a page comes back
 * full, the next. Each answer is held, failure and all, until its turn is read.
 * Sent its tag, an unchanged page answers 304 and the copy held is used again,
 * with nothing parsed. `changed`: a page came anew.
 */
async function readPagesInto(held: HeldPages): Promise<{ rows: LeadRow[]; changed: boolean }> {
  const ask = (offset: number) => {
    const kept = held.get(offset);
    return api.customers(offset, PAGE, kept?.tag ?? "").then(
      (page) => ({ offset, kept, page, error: null as unknown }),
      (error: unknown) => ({ offset, kept, page: null, error }),
    );
  };
  const asked = Array.from({ length: held.size || 2 }, (_, i) => ask(i * PAGE));
  const rows: LeadRow[] = [];
  let changed = false;
  for (let i = 0; ; i++) {
    const { offset, kept, page, error } = await (asked[i] ?? ask(i * PAGE));
    if (!page) throw error;
    // A tag goes out only with a copy to fall back on; a 304 without one is an
    // error, and the rows on screen stay rather than empty.
    if (!page.body && !kept) throw new Error("304 for a page this table never had");
    let leads = kept?.leads ?? [];
    if (page.body) {
      leads = page.body.leads ?? [];
      held.set(offset, { tag: page.tag, leads, at: Date.now() });
      changed = true;
    }
    rows.push(...leads);
    if (leads.length < PAGE) {
      // A list that shrank leaves no page behind it to be used again.
      for (const k of held.keys()) if (k > offset) held.delete(k);
      break;
    }
  }
  return { rows, changed };
}
